import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SignUpData {
  date: string;
  count: number;
}

export interface UserActivityData {
  dau: number;
  wau: number;
  mau: number;
}

export interface TokenUsageData {
  date: string;
  total_tokens: number;
}

export interface CohortAnalysisData {
  cohort_month: string;
  avg_tokens_first_30_days: number;
}

export interface ModelTokenUsageData {
  date: string;
  model_name: string;
  model_provider: string;
  total_tokens: number;
}

export interface ModelUsageByTerritoryData {
  date: string;
  territory: 'USA' | 'Canada' | 'International' | 'Unknown';
  total_tokens: number;
}

export interface ModelUsageByProvinceData {
  date: string;
  province: string;
  total_tokens: number;
}

export interface TokenUsageByLocationData {
  country: string | null;
  province: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  total_tokens: number;
}

export interface TrafficByLocationData {
  country: string | null;
  province: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  event_count: number;
}

export interface AnalyticsData {
  new_signups: SignUpData[];
  user_activity: UserActivityData;
  token_usage: TokenUsageData[];
  cohort_analysis: CohortAnalysisData[];
  model_token_usage: ModelTokenUsageData[];
  model_token_usage_60d: ModelTokenUsageData[];
  model_token_usage_90d: ModelTokenUsageData[];
  model_usage_by_territory: ModelUsageByTerritoryData[];
  model_usage_by_territory_60d: ModelUsageByTerritoryData[];
  model_usage_by_territory_90d: ModelUsageByTerritoryData[];
  model_usage_by_province: ModelUsageByProvinceData[];
  model_usage_by_province_60d: ModelUsageByProvinceData[];
  model_usage_by_province_90d: ModelUsageByProvinceData[];
  token_usage_by_location: TokenUsageByLocationData[];
  traffic_by_location: TrafficByLocationData[];
}

export function useAnalytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.rpc('get_dashboard_analytics');
      if (error) throw error;
      setData(data);
    } catch (e) {
      setError(e as Error);
      console.error("Error fetching analytics data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Add real-time subscription for new usage analytics events
  useEffect(() => {
    // Subscribe to new usage analytics events
    const subscription = supabase
      .channel('usage_analytics_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'usage_analytics',
        },
        (payload) => {
          console.log('New usage analytics event detected:', payload);
          // Refresh analytics data when new usage event is logged
          fetchAnalytics();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchAnalytics]);

  return { data, loading, error, refresh: fetchAnalytics };
}